import { checkStatus, json, CustomerAPI, RxCustomerAPI, RxConversationAPI } from '@driftt/dapper'
import { Map, List, fromJS } from 'immutable'
import { User, Avatars } from './models'
import { UserStatus } from './constants'
import { CLIENT_ID, AUTH_CLIENT_ID } from 'constants/ResourcesConstants'
import { Observable } from 'rxjs/Observable'
import { redirectToLoginPage } from '@driftt/web-auth'

export function parseUser(data) {
  const { gates, avatars, ...rest } = data

  return User({
    gates: Map(gates),
    avatars: Avatars(avatars),
    ...rest,
  })
}

export const api = {
  create(orgId, user) {
    return CustomerAPI.post(`/organizations/${orgId}/users`, user)
      .then(checkStatus)
      .then(json)
      .then(parseUser)
  },

  createByEmail(email, password) {
    const body = { email }
    if (password) {
      Object.assign(body, { password })
    }

    return CustomerAPI.post('/users', body, { auth: false, clientId: true }).then(checkStatus)
  },

  async fetchCurrent() {
    return CustomerAPI.get('/users/me')
      .then(checkStatus)
      .catch((error) => {
        if (error.status === 401 || error.status === 403) {
          redirectToLoginPage(AUTH_CLIENT_ID)
        }
      })
      .then(json)
      .then(parseUser)
  },

  fetch(userId) {
    return CustomerAPI.get(`/users/${userId}`)
      .then(checkStatus)
      .then(json)
      .then(parseUser)
  },

  fetchEmailSignature(userId) {
    return RxCustomerAPI.get(`/users/${userId}/email_signature`)
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },

  fetchOrgSignature({ userId }) {
    return RxCustomerAPI.get(`/users/${userId}/email_signature/v2`)
      .checkStatus()
      .retryWithBackoff(1)
      .map(res => fromJS(res.response))
  },

  fetchOrgMultiSignature({ userId }) {
    return RxCustomerAPI.get(`/users/${userId}/email_signature/all`)
      .checkStatus()
      .retryWithBackoff(1)
      .map(res => fromJS(res.response))
  },

  setEmailSignature(userId, emailSignature) {
    return RxCustomerAPI.post(`/users/${userId}/email_signature`, { emailSignature })
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },

  fetchUsers(orgId) {
    return CustomerAPI.get(`/organizations/${orgId}/users`, {
      status: [UserStatus.INVITED, UserStatus.ENABLED, UserStatus.ARCHIVED],
    })
      .then(checkStatus)
      .then(json)
      .then(data => List(data).map(parseUser))
  },

  update(userId, updates) {
    return CustomerAPI.patch(`/users/${userId}`, updates)
      .then(checkStatus)
      .then(json)
  },

  fetchClearbitForEmail(email) {
    return RxCustomerAPI.get(`/enrichment/single/${email}`)
      .checkStatus()
      .mapResponse(fromJS)
  },

  enrichEmail(email) {
    return CustomerAPI.get(`/enrichment/single/${email}`)
      .then(checkStatus)
      .then(json)
  },

  patchUser(userId, updates) {
    return CustomerAPI.patch(`/users/${userId}/patch`, updates)
      .then(checkStatus)
      .then(json)
      .then(parseUser)
  },

  setOrgUserStatus(userId, updates) {
    return CustomerAPI.patch(`/users/${userId}/patch`, updates).then(checkStatus)
  },

  archive(userId) {
    return CustomerAPI.patch(`/users/${userId}`, { status: UserStatus.ARCHIVED }).then(checkStatus)
  },

  changePassword(userId, currentPassword, newPassword) {
    const handleErrorWithJson = err => json(err).then(parsedResponse => Promise.reject(parsedResponse))

    return CustomerAPI.post(`/users/${userId}/password2?clientId=${CLIENT_ID}`, {
      currentPassword,
      newPassword,
    })
      .then(checkStatus)
      .then(json, handleErrorWithJson)
  },

  uploadAvatar(userId, file) {
    const data = new FormData()
    data.append('file', file)

    return CustomerAPI.post(`/users/${userId}/avatar`, data)
      .then(checkStatus)
      .then(json)
  },

  checkUserExistsByEmail(email) {
    return CustomerAPI.head('/users', { email }, { auth: false })
  },

  checkUserExistsByEmailAndOrgHandle(email, orgHandle) {
    return CustomerAPI.head('/users', { email, orgHandle }, { auth: false })
  },

  sendWelcomeEmail(isMobile = true) {
    return CustomerAPI.post(`/users/welcome?isMobile=${isMobile}`).then(checkStatus)
  },

  turnOffManualAway({ userId }) {
    return RxConversationAPI.post(`/agents/${userId}/turn-off-manual-away`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parseUser)
  },

  fetchGoogleScopes() {
    return RxCustomerAPI.get('/google/calendar/oauth/scopes')
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(fromJS)
  },

  disconnectGmailAccount() {
    return RxCustomerAPI.post('/google/calendar/disconnect/').checkStatus()
  },

  resetEmailSignature(userId) {
    return RxCustomerAPI.post(`/users/${userId}/email_signature`, { default: true })
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },

  fetchIndividualConvoLimit(userId) {
    return RxCustomerAPI.get(`/users/${userId}/convo-limit`)
      .checkStatus()
      .map(res => res.response)
  },

  updateIndividualConvoLimit(userId, limit, remove) {
    return RxCustomerAPI.post(`/users/${userId}/convo-limit?limit=${limit}&remove=${remove}`)
      .checkStatus()
      .map(res => res.response)
  },

  updateUser(user) {
    return RxCustomerAPI.patch(`/users/v2/${user.id}`, user)
      .checkStatus()
      .map(res => res.response)
  }
}



// WEBPACK FOOTER //
// ./src/scripts/app/modules/user/api.js